import { useCallback, useContext, useRef } from 'react'
import { TailSpin } from 'react-loader-spinner'
import { Link, useSearchParams } from 'react-router-dom'
import { Blue300 } from '../../../lib/colors'
import { TestItem } from '../../../components/TestItem'
import {
  SearchTestFragment,
  useGetAvailableTestsByQueryQuery,
} from '../../../generated/urql.client'
import { BasketContext, isTestBasketItem } from '../../../provider/basket'
import { useI18n } from '../../../provider/i18n'
import { AuthContext } from '../../../provider/auth'
import ProfileSelectorModal, {
  ProfileSelectorModalType,
} from '../../../components/ProfileSelectorModal'
import SearchInput from '../../../components/SearchInput'
import { useAtomValue } from 'jotai'
import { profilesSubscriptionAtom } from '../../../store/subscription.atoms'

const SearchTests = () => {
  const [params] = useSearchParams()
  const basket = useContext(BasketContext)
  const auth = useContext(AuthContext)
  const i18n = useI18n()
  const query = params.get('q')
  const [{ data: testsData, fetching: testsFetching }] =
    useGetAvailableTestsByQueryQuery({
      pause: !basket.address?.location || !query,
      variables: {
        point: basket.address?.location || {
          type: 'Point',
          coordinates: [0, 0],
        },
        limit: 1000,
        query: query || '',
      },
    })
  const [{ data: profilesData }] = useAtomValue(profilesSubscriptionAtom)
  const profileSelectorModalRef = useRef<ProfileSelectorModalType>(null)

  const onTestAddClick = useCallback(
    (test: SearchTestFragment) => {
      const profilesCount = profilesData?.client_profile.length || 0

      if (!auth.isLogged || profilesCount === 0) {
        basket.addItem({ test })
      } else if (profilesCount === 1) {
        basket.addItem({ test, profile: profilesData?.client_profile[0] })
      } else {
        profileSelectorModalRef.current?.show({
          onChange: (profile) => {
            basket.addItem({ test, profile })
          },
          filter: (profile) =>
            !basket.items.find(
              (item) =>
                isTestBasketItem(item) &&
                item.test.id === test.id &&
                item.profile?.id === profile.id
            ),
        })
      }
    },
    [auth.isLogged, basket, profilesData?.client_profile]
  )

  const tests = testsData?.search_test || []

  return (
    <div className="mb-10">
      <SearchInput />
      <div className="text-blue-700 my-5 flex gap-1 items-center">
        <span>
          <Link to="/tests">{i18n.t('common.breadcrumbs.mainPage')}</Link>
          {' > '}
          {i18n.t('screens.catalog.search.breadcrumbs.searchResults', {
            query: query || '',
          })}
        </span>
        {testsFetching && <TailSpin color={Blue300} height={16} width={16} />}
      </div>

      {basket.address?.location && !!tests.length && (
        <ul className="md:mx-5 flex flex-col gap-5">
          {tests.map((test) => (
            <li key={test.id}>
              <TestItem
                test={test}
                variant="blue"
                onTestAddClick={() => onTestAddClick(test)}
                showAddButton={
                  auth.isLogged
                    ? basket.items
                        .filter(isTestBasketItem)
                        .filter(({ test: { id } }) => test.id === id).length <
                      (profilesData?.client_profile.length || 0)
                    : !basket.items.find(
                        (item) =>
                          isTestBasketItem(item) && test.id === item.test.id
                      )
                }
              />
            </li>
          ))}
        </ul>
      )}
      <ProfileSelectorModal ref={profileSelectorModalRef} />
    </div>
  )
}

export default SearchTests
